import { lazy, type LazyExoticComponent, type ComponentType } from 'react';

export type Role = 'manager' | 'member' | 'admin';

export interface Article {
  id: string;
  title: string;
  path: string;
  component: LazyExoticComponent<ComponentType>;
  roles?: Role[];
}

export interface SubCategory {
  id: string;
  title: string;
  icon: string;
  articles: Article[];
}

export interface Category {
  id: string;
  title: string;
  icon: string;
  articles?: Article[];
  subcategories?: SubCategory[];
}

export const categories: Category[] = [
  {
    id: 'quick-start',
    title: 'クイックスタート',
    icon: 'PlayCircle',
    articles: [
      {
        id: 'quick-start-manager-guide',
        title: 'はじめての TONOME Core（マネージャー）',
        path: '/quick-start/manager-guide',
        component: lazy(() => import('./quick-start/manager-guide')),
        roles: ['manager'],
      },
      {
        id: 'quick-start-member-guide',
        title: 'はじめての TONOME Core（メンバー）',
        path: '/quick-start/member-guide',
        component: lazy(() => import('./quick-start/member-guide')),
        roles: ['member'],
      },
    ],
  },
  {
    id: 'getting-started',
    title: 'はじめに',
    icon: 'Rocket',
    articles: [
      {
        id: 'login',
        title: 'TONOME Coreへログインする',
        path: '/getting-started/login',
        component: lazy(() => import('./getting-started/login')),
        roles: ['manager', 'member', 'admin'],
      },
      {
        id: 'calendar-integration',
        title: 'カレンダーと連携する（未連携で使用する場合は不要）',
        path: '/getting-started/calendar-integration',
        component: lazy(() => import('./getting-started/calendar-integration')),
        roles: ['manager', 'member'],
      },
      {
        id: 'add-members',
        title: 'メンバーを追加する（マネージャーのみ）',
        path: '/getting-started/add-members',
        component: lazy(() => import('./getting-started/add-members')),
        roles: ['manager'],
      },
      {
        id: 'system-requirements',
        title: 'TONOME Coreの推奨環境',
        path: '/getting-started/system-requirements',
        component: lazy(() => import('./getting-started/system-requirements')),
        roles: ['manager', 'member', 'admin'],
      },
    ],
  },
  {
    id: 'features',
    title: 'TONOME Coreの機能',
    icon: 'Sparkles',
    subcategories: [
      {
        id: 'dashboard',
        title: 'ダッシュボード',
        icon: 'LayoutDashboard',
        articles: [
          {
            id: 'dashboard-manager',
            title: 'ダッシュボードを見る（マネージャー）',
            path: '/features/dashboard/manager',
            component: lazy(() => import('./features/dashboard/manager')),
            roles: ['manager'],
          },
          {
            id: 'dashboard-member',
            title: 'ダッシュボードを見る（メンバー）',
            path: '/features/dashboard/member',
            component: lazy(() => import('./features/dashboard/member')),
            roles: ['member'],
          },
        ],
      },
      {
        id: 'ai-mentor',
        title: 'AIメンター機能',
        icon: 'Bot',
        articles: [
          {
            id: 'ai-mentor-ask',
            title: 'AIメンターに質問や相談をする（マネージャー）',
            path: '/features/ai-mentor/ask',
            component: lazy(() => import('./features/ai-mentor/ask')),
            roles: ['manager'],
          },
          {
            id: 'ai-mentor-ask-about-member',
            title: 'AIメンターにメンバーについて質問や相談をする（マネージャー）',
            path: '/features/ai-mentor/ask-about-member',
            component: lazy(() => import('./features/ai-mentor/ask-about-member')),
            roles: ['manager'],
          },
        ],
      },
      {
        id: 'profile',
        title: 'プロフィール',
        icon: 'User',
        articles: [
          {
            id: 'profile-settings',
            title: 'プロフィールを設定する',
            path: '/features/profile/settings',
            component: lazy(() => import('./features/profile/settings')),
            roles: ['member'],
          },
          {
            id: 'profile-supervisor-section',
            title: '上司入力欄について（マネージャー）',
            path: '/features/profile/supervisor-section',
            component: lazy(() => import('./features/profile/supervisor-section')),
            roles: ['manager'],
          },
        ],
      },
      {
        id: 'hierarchy',
        title: '上司・部下',
        icon: 'Users',
        articles: [
          {
            id: 'hierarchy-manage',
            title: '上司・部下を管理する',
            path: '/features/hierarchy/manage',
            component: lazy(() => import('./features/hierarchy/manage')),
            roles: ['manager', 'member'],
          },
        ],
      },
      {
        id: 'one-on-one',
        title: '1on1機能',
        icon: 'Users',
        articles: [
          {
            id: 'one-on-one-register',
            title: '1on1予定を登録する（カレンダー連携）',
            path: '/features/one-on-one/register',
            component: lazy(() => import('./features/one-on-one/register')),
            roles: ['manager', 'member'],
          },
          {
            id: 'one-on-one-without-calendar',
            title: '1on1予定を登録する（カレンダー未連携）',
            path: '/features/one-on-one/without-calendar',
            component: lazy(() => import('./features/one-on-one/without-calendar')),
            roles: ['manager', 'member'],
          },
          {
            id: 'one-on-one-agenda',
            title: '1on1のアジェンダを登録・編集する',
            path: '/features/one-on-one/agenda',
            component: lazy(() => import('./features/one-on-one/agenda')),
            roles: ['manager'],
          },
          {
            id: 'one-on-one-agenda-member',
            title: 'アジェンダを追加する（メンバー）',
            path: '/features/one-on-one/agenda-member',
            component: lazy(() => import('./features/one-on-one/agenda-member')),
            roles: ['member'],
          },
          {
            id: 'one-on-one-conduct',
            title: '1on1をリモートで実施する（Google Meet / Teams）',
            path: '/features/one-on-one/conduct',
            component: lazy(() => import('./features/one-on-one/conduct')),
            roles: ['manager', 'member'],
          },
          {
            id: 'one-on-one-conduct-webrtc',
            title: '1on1をリモートで実施する（Web会議(TONOME)）',
            path: '/features/one-on-one/conduct-webrtc',
            component: lazy(() => import('./features/one-on-one/conduct-webrtc')),
            roles: ['manager', 'member'],
          },
          {
            id: 'one-on-one-conduct-in-person',
            title: '1on1を対面で実施する',
            path: '/features/one-on-one/conduct-in-person',
            component: lazy(() => import('./features/one-on-one/conduct-in-person')),
            roles: ['manager', 'member'],
          },
          {
            id: 'one-on-one-quick-start',
            title: '予定を決めずに今すぐ1on1を始める',
            path: '/features/one-on-one/quick-start',
            component: lazy(() => import('./features/one-on-one/quick-start')),
            roles: ['manager', 'member'],
          },
          {
            id: 'one-on-one-mobile-recording',
            title: 'スマートフォンで1on1を録音する',
            path: '/features/one-on-one/mobile-recording',
            component: lazy(() => import('./features/one-on-one/mobile-recording')),
            roles: ['manager', 'member'],
          },
          {
            id: 'one-on-one-reschedule',
            title: '1on1の日時を変更する',
            path: '/features/one-on-one/reschedule',
            component: lazy(() => import('./features/one-on-one/reschedule')),
            roles: ['manager', 'member'],
          },
          {
            id: 'one-on-one-delete-schedule',
            title: '1on1の回を1件だけ削除する',
            path: '/features/one-on-one/delete-schedule',
            component: lazy(() => import('./features/one-on-one/delete-schedule')),
            roles: ['manager'],
          },
          {
            id: 'one-on-one-cancel',
            title: '1on1をキャンセル・アーカイブする',
            path: '/features/one-on-one/cancel',
            component: lazy(() => import('./features/one-on-one/cancel')),
            roles: ['manager', 'member'],
          },
          {
            id: 'one-on-one-google-meet-setup',
            title: 'Google Meetの自動メモ日本語化設定方法',
            path: '/features/one-on-one/google-meet-setup',
            component: lazy(() => import('./features/one-on-one/google-meet-setup')),
            roles: ['manager', 'member'],
          },
          {
            id: 'one-on-one-recording-location',
            title: '録画データの保存場所',
            path: '/features/one-on-one/recording-location',
            component: lazy(() => import('./features/one-on-one/recording-location')),
            roles: ['manager', 'member'],
          },
        ],
      },
      {
        id: 'objectives',
        title: '目標',
        icon: 'Target',
        articles: [
          {
            id: 'objectives-manage',
            title: '目標を設定・管理する',
            path: '/features/objectives/manage',
            component: lazy(() => import('./features/objectives/manage')),
            roles: ['manager', 'member'],
          },
        ],
      },
      {
        id: 'monthly-report',
        title: 'マンスリーレポート',
        icon: 'FileBarChart',
        articles: [
          {
            id: 'monthly-report-view',
            title: 'マンスリーレポートを確認する',
            path: '/features/monthly-report/view',
            component: lazy(() => import('./features/monthly-report/view')),
            roles: ['manager'],
          },
        ],
      },
      {
        id: 'personal-report',
        title: 'パーソナルレポート',
        icon: 'FileUser',
        articles: [
          {
            id: 'personal-report-view',
            title: 'パーソナルレポートを確認する',
            path: '/features/personal-report/view',
            component: lazy(() => import('./features/personal-report/view')),
            roles: ['member'],
          },
        ],
      },
      {
        id: 'manager-analytics',
        title: '1on1利用分析',
        icon: 'BarChart3',
        articles: [
          {
            id: 'manager-analytics-view',
            title: '1on1利用分析を確認する',
            path: '/features/manager-analytics/view',
            component: lazy(() => import('./features/manager-analytics/view')),
            roles: ['manager'],
          },
        ],
      },
      {
        id: 'handover',
        title: '申し送り',
        icon: 'FileOutput',
        articles: [
          {
            id: 'handover-create',
            title: '申し送りを作成・共有する',
            path: '/features/handover/create',
            component: lazy(() => import('./features/handover/create')),
            roles: ['manager'],
          },
        ],
      },
      {
        id: 'integration',
        title: '外部連携機能',
        icon: 'Link',
        articles: [
          {
            id: 'integration-kaonavi-user',
            title: 'カオナビと連携する（ユーザー設定編）',
            path: '/features/integration/kaonavi-user',
            component: lazy(() => import('./features/integration/kaonavi-user')),
            roles: ['manager', 'member'],
          },
        ],
      },
    ],
  },
  {
    id: 'troubleshooting',
    title: 'トラブルシューティング',
    icon: 'Wrench',
    articles: [
      {
        id: 'teams-meeting-split',
        title: '複数のTeams会議に別れた場合の対処法',
        path: '/troubleshooting/teams-meeting-split',
        component: lazy(() => import('./troubleshooting/teams-meeting-split')),
        roles: ['manager', 'member'],
      },
      {
        id: 'in-person-recording-checklist',
        title: '対面録音実施前のチェックリスト',
        path: '/troubleshooting/in-person-recording-checklist',
        component: lazy(() => import('./troubleshooting/in-person-recording-checklist')),
        roles: ['manager', 'member'],
      },
    ],
  },
  {
    id: 'admin',
    title: '管理者向け',
    icon: 'Settings',
    subcategories: [
      {
        id: 'company-info',
        title: '会社情報',
        icon: 'Building2',
        articles: [
          {
            id: 'admin-company-info-settings',
            title: '会社情報を設定する',
            path: '/admin/company-info/settings',
            component: lazy(() => import('./admin/company-info/settings')),
            roles: ['admin'],
          },
        ],
      },
      {
        id: 'team-analytics',
        title: 'チーム分析機能',
        icon: 'BarChart3',
        articles: [
          {
            id: 'admin-team-analytics-status',
            title: '1on1の実施状況を把握する',
            path: '/admin/team-analytics/status',
            component: lazy(() => import('./admin/team-analytics/status')),
            roles: ['admin'],
          },
          {
            id: 'admin-team-analytics-usage-status',
            title: '利用状況の見方',
            path: '/admin/team-analytics/usage-status',
            component: lazy(() => import('./admin/team-analytics/usage-status')),
            roles: ['admin'],
          },
        ],
      },
      {
        id: 'user-management',
        title: 'ユーザー管理機能',
        icon: 'UserCog',
        articles: [
          {
            id: 'admin-user-management-invite',
            title: 'ユーザーを招待する',
            path: '/admin/user-management/invite',
            component: lazy(() => import('./admin/user-management/invite')),
            roles: ['admin'],
          },
          {
            id: 'admin-user-management-permissions',
            title: 'ユーザーの権限を変更する',
            path: '/admin/user-management/permissions',
            component: lazy(() => import('./admin/user-management/permissions')),
            roles: ['admin'],
          },
          {
            id: 'admin-user-management-cancel-invitation',
            title: '招待を取り消す',
            path: '/admin/user-management/cancel-invitation',
            component: lazy(() => import('./admin/user-management/cancel-invitation')),
            roles: ['admin'],
          },
          {
            id: 'admin-user-management-disable',
            title: 'ユーザーを無効にする',
            path: '/admin/user-management/disable',
            component: lazy(() => import('./admin/user-management/disable')),
            roles: ['admin'],
          },
          {
            id: 'admin-user-management-permission-matrix',
            title: 'ユーザーの権限と使える機能',
            path: '/admin/user-management/permission-matrix',
            component: lazy(() => import('./admin/user-management/permission-matrix')),
            roles: ['admin'],
          },
        ],
      },
      {
        id: 'team-management',
        title: '部署管理機能',
        icon: 'UsersRound',
        articles: [
          {
            id: 'admin-team-management-register',
            title: '新しい部署を登録する',
            path: '/admin/team-management/register',
            component: lazy(() => import('./admin/team-management/register')),
            roles: ['admin'],
          },
          {
            id: 'admin-team-management-edit',
            title: '部署を編集する',
            path: '/admin/team-management/edit',
            component: lazy(() => import('./admin/team-management/edit')),
            roles: ['admin'],
          },
          {
            id: 'admin-team-management-delete',
            title: '部署を削除する',
            path: '/admin/team-management/delete',
            component: lazy(() => import('./admin/team-management/delete')),
            roles: ['admin'],
          },
        ],
      },
      {
        id: 'advice-settings',
        title: '1on1設定',
        icon: 'Settings',
        articles: [
          {
            id: 'admin-advice-settings-manage',
            title: '1on1のアドバイス設定を変更する',
            path: '/admin/advice-settings/manage',
            component: lazy(() => import('./admin/advice-settings/manage')),
            roles: ['admin'],
          },
        ],
      },
      {
        id: 'elearning',
        title: '学習コンテンツ管理',
        icon: 'BookOpen',
        articles: [
          {
            id: 'admin-elearning-manage',
            title: '学習コンテンツを管理する',
            path: '/admin/elearning/manage',
            component: lazy(() => import('./admin/elearning/manage')),
            roles: ['admin'],
          },
        ],
      },
      {
        id: 'security',
        title: 'セキュリティ',
        icon: 'Shield',
        articles: [
          {
            id: 'admin-security-settings',
            title: '多要素認証（MFA）を設定する',
            path: '/admin/security/settings',
            component: lazy(() => import('./admin/security/settings')),
            roles: ['admin'],
          },
          {
            id: 'admin-security-ip-restriction',
            title: 'IP制限を設定する',
            path: '/admin/security/ip-restriction',
            component: lazy(() => import('./admin/security/ip-restriction')),
            roles: ['admin'],
          },
        ],
      },
      {
        id: 'admin-integration',
        title: '外部連携機能',
        icon: 'Link',
        articles: [
          {
            id: 'admin-integration-kaonavi-tenant',
            title: 'カオナビと連携する（テナント設定編）',
            path: '/admin/integration/kaonavi-tenant',
            component: lazy(() => import('./admin/integration/kaonavi-tenant')),
            roles: ['admin'],
          },
          {
            id: 'admin-integration-kaonavi-settings',
            title: 'カオナビと連携する（カオナビ設定編）',
            path: '/admin/integration/kaonavi-settings',
            component: lazy(() => import('./admin/integration/kaonavi-settings')),
            roles: ['admin'],
          },
        ],
      },
      {
        id: 'plan',
        title: '契約プラン',
        icon: 'CreditCard',
        articles: [
          {
            id: 'admin-plan-info',
            title: '契約プラン情報を確認する',
            path: '/admin/plan/info',
            component: lazy(() => import('./admin/plan/info')),
            roles: ['admin'],
          },
        ],
      },
    ],
  },
];
/** レジストリから全記事をフラットリストとして取得（ルーティング用） */
export function getAllArticles(): Article[] {
  return categories.flatMap((cat) => [
    ...(cat.articles ?? []),
    ...(cat.subcategories?.flatMap((sub) => sub.articles) ?? []),
  ]);
}
/** 指定ロールに該当する記事のみでカテゴリをフィルタする */
export function getCategoriesForRole(role: Role): Category[] {
  return categories
    .map((cat) => {
      const filteredArticles = cat.articles?.filter((a) => a.roles?.includes(role));
      const filteredSubcategories = cat.subcategories
        ?.map((sub) => ({
          ...sub,
          articles: sub.articles.filter((a) => a.roles?.includes(role)),
        }))
        .filter((sub) => sub.articles.length > 0);
      return {
        ...cat,
        articles: filteredArticles,
        subcategories: filteredSubcategories,
      };
    })
    .filter((cat) => Boolean(cat.articles?.length || cat.subcategories?.length));
}

export const roleLabels: Record<Role, string> = {
  manager: 'マネージャー',
  member: 'メンバー',
  admin: 'テナント管理者',
};
